import { toggleSchemeDragItemByID3, useSchemeDragItem3 } from "@mobx/hooks/useShemeDragItem3";
import cn from "classnames";
import { observer } from "mobx-react-lite";
import {
  CSSProperties,
  MutableRefObject,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import styles from "./index.module.scss";
import { ScrollInfo } from "./row";
import * as typ from "./types";

export interface OwnerControl {
  dragStart(
    ev: typ.ItemMouseEvent,
    el: MutableRefObject<typ.HTMLItemElement | null>,
    item: typ.Item
  ): void;
  itemExtDragOver(ev: typ.ItemDragEvent, item: typ.Item): void;

  isDrag: boolean;
}

export interface ItemProps {
  item: typ.Item;
  control: OwnerControl;
  columnWidth: number;
  dragType: string;
  scroll: ScrollInfo;
  scrollX: number;

  className?: string;
  style?: CSSProperties;
  unlocked?: boolean;
  shift?: boolean;
  setHeight?(h: number): void;
}

//export const measureTime = new MeasureTime("GROUP RENDER:");

const useItemControl = (props: ItemProps) => {
  const [hasDrag, setHasDrag] = useState(false);
  const self = useRef<HTMLDivElement | null>(null);
  const unshiftedRect = useRef<DOMRect | null>(null);
  const [redraw, setRedraw] = useState(false);
  const schemeDragItem = useSchemeDragItem3(
    "rack",
    props.item.id,
    self,
    props.item.userInfo
  );

  useEffect(() => {
    setTimeout(() => setRedraw(true), 300);
    //measureTime.tryFinish();
  }, []);

  useEffect(() => {
    if (props.setHeight && self.current?.offsetHeight) {
      props.setHeight(self.current.offsetHeight);
    }
  }, [props.setHeight, self.current?.offsetHeight]);

  useEffect(() => {
    if (!props.scroll.target) return;
    const cb = () => setRedraw((x) => !x);
    const obsrv = new ResizeObserver(cb);
    obsrv.observe(props.scroll.target);
    return () => obsrv.unobserve(props.scroll.target!);
  }, [props.scroll.target]);

  useEffect(() => {
    if (!props.shift && self.current)
      unshiftedRect.current = self.current.getBoundingClientRect();
  }, [props.shift, self.current]);

  useEffect(() => {
    if (
      self.current &&
      props.scroll.needScroll &&
      props.scroll.target &&
      props.scroll.id === props.item.id
    ) {
      const left = self.current.offsetLeft;
      props.scroll.target.scrollTo({ left, behavior: "smooth" });
    }
  }, [self.current, props.scroll]);

  useEffect(() => {
    if (self.current) {
      const el = self.current;
      props.item.rect = () => {
        let r: DOMRect;
        if (props.shift && unshiftedRect.current) r = unshiftedRect.current;
        else r = el.getBoundingClientRect();
        //console.log("ITEM RECT", props.item.id, r);
        return r;
      };
    }
  }, [self.current, props.item, props.shift, unshiftedRect.current]);

  const invisible = useMemo(() => {
    if (!self.current || !props.scroll.target) return true;
    const rect = self.current.getBoundingClientRect();
    const trect = props.scroll.target.getBoundingClientRect();
    const right = rect.left + props.item.numCols * props.columnWidth;
    const inv = rect.left > trect.right || right < trect.left;
    return inv;
  }, [redraw, props.scrollX, props.scroll.target]);

  //useEffect(() => console.log("INVISIBLE", props.item.id, invisible), [invisible]);
  return {
    self,
    hasDrag,
    invisible,
    isSelectedForSchemeDrag: schemeDragItem.isSelectedForSchemeDrag,
    dragID: schemeDragItem.dragID,

    dragEnter(e: typ.ItemDragEvent) {
      if (
        !e.dataTransfer.items[0] ||
        e.dataTransfer.items[0].type !== props.dragType
      )
        return;
      if (self.current !== e.target) return;
      setHasDrag(true);
    },

    dragOver(e: typ.ItemDragEvent) {
      if (
        !e.dataTransfer.items[0] ||
        e.dataTransfer.items[0].type !== props.dragType
      )
        return;
      if (self.current !== e.target) return;
      setHasDrag(true);
      props.control.itemExtDragOver(e, props.item);
    },

    dragLeave(e: typ.ItemDragEvent) {
      if (
        !e.dataTransfer.items[0] ||
        e.dataTransfer.items[0].type !== props.dragType
      )
        return;
      if (self.current !== e.target) return;
      setHasDrag(false);
    },

    drop(e: typ.ItemDragEvent) {
      setHasDrag(false);
    },

    mouseDown(e: typ.ItemMouseEvent) {
      const innerDragEl = (e.target as HTMLElement | null)?.closest?.("[data-drag-id]") as HTMLElement | null;
      const pointDragEl = document
        .elementsFromPoint(e.clientX, e.clientY)
        .find((el) =>
          el instanceof HTMLElement &&
          el !== self.current &&
          !!el.getAttribute("data-drag-id") &&
          !!self.current?.contains(el)
        ) as HTMLElement | undefined;
      const nestedDragID = pointDragEl?.getAttribute("data-drag-id");
      if (nestedDragID && nestedDragID !== self.current?.getAttribute("data-drag-id")) {
        if (e.ctrlKey) {
          toggleSchemeDragItemByID3(nestedDragID);
          e.stopPropagation();
          e.preventDefault();
        }
        return;
      }
      if (innerDragEl && self.current && innerDragEl !== self.current && self.current.contains(innerDragEl)) {
        return;
      }
      if (e.ctrlKey) {
        schemeDragItem.mouseDown(e);
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      if (schemeDragItem.isSelectedForSchemeDrag) {
        e.stopPropagation();
        e.preventDefault();
        return;
      }
      if (!e.ctrlKey) {
        props.unlocked && props.control.dragStart(e, self, props.item);
      }
    },
  };
};

export const Item = observer((props: ItemProps) => {
  const ctrl = useItemControl(props);
  return ctrl.invisible ? (
    <div
      ref={ctrl.self}
      className={cn(props.className, styles.item, {
        [styles.unlocked]: props.unlocked || ctrl.hasDrag,
      })}
      style={{
        ...props.style,
        minWidth: props.item.numCols * props.columnWidth,
        padding: props.columnWidth,
      }}
      data-row={props.item.pos.row}
      data-id={props.item.id}
    >
      <div
        style={{
          border: "1px solid grey",
          minWidth: "100%",
          minHeight: "100%",
        }}
      ></div>
    </div>
  ) : (
    <div
      ref={ctrl.self}
      className={cn(props.className, styles.item, {
        [styles.unlocked]: props.unlocked || ctrl.hasDrag,
        [styles.schemeDrag]: ctrl.isSelectedForSchemeDrag,
      })}
      style={{
        ...props.style,
        minWidth: props.item.numCols * props.columnWidth,
        // overflow: "hidden",
      }}
      data-row={props.item.pos.row}
      data-id={props.item.id}
      onMouseDown={(e) => ctrl.mouseDown(e)}
      onDragEnter={(e) => ctrl.dragEnter(e)}
      onDragOver={(e) => ctrl.dragOver(e)}
      onDragLeave={(e) => ctrl.dragLeave(e)}
      onDrop={(e) => ctrl.drop(e)}
      data-drag-id={ctrl.dragID}
    >
      {props.item.render}
    </div>
  );
});

export default Item;
